import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { AppFrame } from '../components/AppNav';
import { Banner, Field, Loading } from '../components/ui';
import { formatDateTime } from '../lib/format';
import { useSession } from '../state/session';
import { useToast } from '../state/toast';
import { useMfaGuard } from '../components/MfaGuard';

export function Settings() {
  const { orgId, org, user, refresh, can } = useSession();
  const { notify } = useToast();
  const { guard, challenge } = useMfaGuard();
  const [members, setMembers] = useState<any[]>([]);
  const [retention, setRetention] = useState<any>(null);
  const [form, setForm] = useState({ name: '', brand_color: '#2456c7',
                                     auth_policy: 'email_otp', sms_otp_enabled: false });
  const [invite, setInvite] = useState({ email: '', role: 'sender' });
  const [mfa, setMfa] = useState<any>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    const [memberData, retentionData] = await Promise.all([
      api.get(`/organizations/${orgId}/members`).catch(() => ({ members: [] })),
      can('retention:manage')
        ? api.get(`/organizations/${orgId}/retention`).catch(() => null)
        : Promise.resolve(null),
    ]);
    setMembers(memberData.members);
    setRetention(retentionData);
  }, [orgId, can]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (org) {
      setForm({
        name: org.name, brand_color: org.brand_color,
        auth_policy: org.auth_policy, sms_otp_enabled: org.sms_otp_enabled,
      });
    }
  }, [org]);

  const saveOrg = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      await guard(async () => {
        await api.patch(`/organizations/${orgId}`, form);
        await refresh();
        notify('Organization settings saved.', 'ok');
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not save.');
    } finally { setBusy(false); }
  };

  const sendInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      await guard(async () => {
        await api.post(`/organizations/${orgId}/members`, invite);
        setInvite({ email: '', role: 'sender' });
        notify('Invitation sent.', 'ok');
        await load();
      });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Could not send the invitation.');
    } finally { setBusy(false); }
  };

  const startMfa = async () => {
    setMfa(await api.post('/auth/mfa/begin'));
  };

  const confirmMfa = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post('/auth/mfa/confirm', { code: mfaCode });
      setMfa(null); setMfaCode('');
      await refresh();
      notify('Two-factor authentication enabled.', 'ok');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That code did not match.');
    } finally { setBusy(false); }
  };

  if (!org) return <AppFrame><div className="shell section"><Loading /></div></AppFrame>;

  return (
    <AppFrame>
      {challenge}
      <div className="shell section stack-l">
        <h1 style={{ margin: 0 }}>Settings</h1>
        {error && <Banner tone="danger" title="That did not work">{error}</Banner>}

        <form className="card stack" onSubmit={saveOrg}>
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Organization</h2>
          <Field label="Name" id="org-name">
            <input id="org-name" className="input" value={form.name}
                   disabled={!can('org:manage')}
                   onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </Field>
          <Field label="Brand colour" id="org-color"
                 hint="Used on the signing page your recipients see.">
            <input id="org-color" className="input" type="color" value={form.brand_color}
                   disabled={!can('org:manage')}
                   style={{ maxWidth: 120, height: 48, padding: 4 }}
                   onChange={(event) => setForm({ ...form, brand_color: event.target.value })} />
          </Field>
          <Field label="Default recipient authentication" id="org-auth"
                 hint="An emailed link or email code demonstrates access to that mailbox. It is not verified legal identity.">
            <select id="org-auth" className="select" value={form.auth_policy}
                    disabled={!can('org:manage')}
                    onChange={(event) => setForm({ ...form, auth_policy: event.target.value })}>
              <option value="email_link">Personal emailed link</option>
              <option value="email_otp">Email one-time passcode</option>
              <option value="sms_otp">SMS one-time passcode</option>
            </select>
          </Field>
          <label className="checkbox">
            <input type="checkbox" checked={form.sms_otp_enabled} disabled={!can('org:manage')}
                   onChange={(event) =>
                     setForm({ ...form, sms_otp_enabled: event.target.checked })} />
            <span className="checkbox__text">Allow SMS one-time passcodes for this organization</span>
          </label>
          {can('org:manage') && (
            <button className="btn btn--primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          )}
        </form>

        <div className="card stack">
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>People</h2>
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>2FA</th></tr></thead>
              <tbody>
                {members.map((member: any) => (
                  <tr key={member.id}>
                    <td>{member.name}</td>
                    <td className="truncate" style={{ maxWidth: 220 }}>{member.email}</td>
                    <td>
                      {can('members:manage') && member.id !== user?.id ? (
                        <select className="select" style={{ minHeight: 36, padding: '4px 8px' }}
                                value={member.role}
                                onChange={async (event) => {
                                  const role = event.target.value;
                                  await guard(async () => {
                                    await api.patch(
                                      `/organizations/${orgId}/members/${member.id}`,
                                      { role });
                                    await load();
                                  });
                                }}>
                          {['owner', 'admin', 'sender', 'compliance', 'viewer'].map((role) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      ) : <span className="pill">{member.role}</span>}
                    </td>
                    <td>{member.mfa_enabled
                      ? <span className="pill pill--ok">On</span>
                      : <span className="pill">Off</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {can('members:manage') && (
            <form className="row" onSubmit={sendInvite}>
              <div style={{ flex: '1 1 220px' }}>
                <Field label="Invite by email" id="invite-email">
                  <input id="invite-email" className="input" type="email" value={invite.email}
                         onChange={(event) => setInvite({ ...invite, email: event.target.value })} />
                </Field>
              </div>
              <div style={{ flex: '0 1 160px' }}>
                <Field label="Role" id="invite-role">
                  <select id="invite-role" className="select" value={invite.role}
                          onChange={(event) => setInvite({ ...invite, role: event.target.value })}>
                    <option value="sender">Sender</option>
                    <option value="admin">Admin</option>
                    <option value="compliance">Compliance reviewer</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </Field>
              </div>
              <button className="btn btn--primary" style={{ alignSelf: 'end' }}
                      disabled={busy || !invite.email}>Invite</button>
            </form>
          )}
        </div>

        <div className="card stack">
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Two-factor authentication</h2>
          <p className="small muted" style={{ margin: 0 }}>
            Required for billing, member management, API keys and retention actions
            once enabled on your account.
          </p>
          {user?.mfa_enabled ? (
            <Banner tone="ok" title="Enabled">
              Your account uses an authenticator app for sensitive operations.
            </Banner>
          ) : mfa ? (
            <form className="stack" onSubmit={confirmMfa}>
              <p className="small muted" style={{ margin: 0 }}>
                Add this secret to your authenticator app, then enter the current code.
              </p>
              <pre className="codebox">{mfa.secret}</pre>
              <Field label="Current code" id="mfa-code" required>
                <input id="mfa-code" className="input" inputMode="numeric" maxLength={6}
                       required value={mfaCode}
                       onChange={(event) => setMfaCode(event.target.value)} />
              </Field>
              <button className="btn btn--primary" disabled={busy || mfaCode.length !== 6}>
                Enable
              </button>
            </form>
          ) : (
            <button className="btn" onClick={() => void startMfa()}>
              Set up authenticator app
            </button>
          )}
        </div>

        {retention && (
          <div className="card stack">
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Retention</h2>
            {/* Short operational cleanup windows only. How long completed
                agreements are kept is stated in the Terms of Service and
                nowhere else — a figure on a settings page reads as a promise
                however it is worded, and would drift from the version the
                customer actually agreed to. */}
            <div className="kv">
              <div className="kv__k">Drafts</div>
              <div className="kv__v">{retention.policy.draft_days} days</div>
              <div className="kv__k">Guest preparation drafts</div>
              <div className="kv__v">{retention.policy.guest_draft_days} days</div>
              <div className="kv__k">Temporary renders</div>
              <div className="kv__v">{retention.policy.temp_render_hours} hours</div>
            </div>
            <p className="small muted" style={{ margin: 0 }}>
              Completed agreements are kept under the{' '}
              <a href={retention.policy.retention_terms_url}
                 target="_blank" rel="noopener noreferrer">Terms of Service</a>.
              Export any agreement at any time — an export verifies on its own.
            </p>
            {retention.holds.length > 0 && (
              <>
                <h3 className="card__title">Active legal holds</h3>
                <div className="table-wrap">
                  <table className="table">
                    <thead><tr><th>Scope</th><th>Authority</th><th>Placed</th></tr></thead>
                    <tbody>
                      {retention.holds.map((hold: any) => (
                        <tr key={hold.id}>
                          <td>{hold.scope}</td>
                          <td>{hold.authority}</td>
                          <td className="nowrap">{formatDateTime(hold.placed_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </AppFrame>
  );
}
